import React, { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import {
  RELACIONES, ESTADOS_VE, AFECTADO_OPTS, ESTADO_ACTUAL_OPTS, SI_NO, MIEMBRO_OPTS, LADO_OPTS,
  NIVEL_INFERIOR_OPTS, NIVEL_SUPERIOR_OPTS, MUNON_OPTS, APOYO_OPTS, ASISTIR_OPTS, compressImage, formatDate,
} from "@/lib/constants";
import { TextField, RadioCards, Dropdown, SearchableDropdown } from "@/components/fields";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Camera, X, Loader2, CheckCircle2, Copy, Pencil,
} from "lucide-react";

const EMPTY = {
  reg_nombre: "", reg_relacion: "", reg_telefono: "",
  pac_nombre: "", pac_cedula: "", pac_edad: "", pac_sexo: "", pac_altura: "", pac_peso: "",
  pac_ciudad: "", pac_estado: "", pac_telefono1: "", pac_familiar: "",
  afectado_terremoto: "", estado_actual: "", ya_amputado: "", fecha_amputacion: "", hospital: "",
  miembro_afectado: "", lado_afectado: "", nivel_inferior: "", nivel_superior: "", estado_munon: "",
  menor_edad: "", apoyo_familiar: "", puede_asistir: "", consentimiento: false,
};

const STORAGE = "proam_form_v1";

export default function Wizard() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem(STORAGE));
      return s?.form ? { ...EMPTY, ...s.form } : EMPTY;
    } catch {
      return EMPTY;
    }
  });
  const [photos, setPhotos] = useState([]); // {file_id, url}
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [numero, setNumero] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(STORAGE, JSON.stringify({ form }));
  }, [form]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
  }, [step]);

  const set = (k, v) => {
    setForm((f) => {
      const nf = { ...f, [k]: v };
      if (k === "ya_amputado" && v !== "Sí") nf.fecha_amputacion = "";
      return nf;
    });
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const showsInferior = form.miembro_afectado === "Miembro inferior" || form.miembro_afectado === "Ambos";
  const showsSuperior = form.miembro_afectado === "Miembro superior" || form.miembro_afectado === "Ambos";

  function validate(target) {
    const e = {};
    const NAME_RE = /^[A-Za-zÀ-ÿñÑ\s.'-]{3,}$/;
    const req = (k, msg) => { if (!String(form[k] ?? "").trim()) e[k] = msg || "Este campo es obligatorio"; };
    const name = (k, msg) => { const val = String(form[k] ?? "").trim(); if (!val) e[k] = "Este campo es obligatorio"; else if (!NAME_RE.test(val)) e[k] = msg || "Solo se permiten letras"; };
    const range = (k, min, max, msg) => { const n = Number(form[k]); if (!form[k] || isNaN(n) || n < min || n > max) e[k] = msg; };
    const phone = (k) => {
      const d = String(form[k] ?? "").replace(/[\s\-()]/g, "");
      if (!d) e[k] = "Este campo es obligatorio";
      else if (!/^\+?\d{7,15}$/.test(d)) e[k] = "Ingrese un número de teléfono válido";
    };
    if (target === 1) { name("reg_nombre", "El nombre solo puede contener letras"); req("reg_relacion", "Seleccione una opción"); phone("reg_telefono"); }
    if (target === 2) {
      name("pac_nombre", "El nombre solo puede contener letras");
      if (form.pac_cedula && !/^[VEJGP]?-?\d{5,10}$/i.test(form.pac_cedula.trim())) e.pac_cedula = "Cédula inválida (ej: V-12345678)";
      const edad = Number(form.pac_edad);
      if (!form.pac_edad || isNaN(edad) || !Number.isInteger(edad) || edad <= 0 || edad > 120) e.pac_edad = "Ingrese una edad válida (1–120)";
      req("pac_sexo", "Seleccione una opción");
      range("pac_altura", 30, 250, "La altura debe estar entre 30 y 250 cm");
      range("pac_peso", 2, 400, "El peso debe estar entre 2 y 400 kg");
      req("pac_estado", "Seleccione un estado"); phone("pac_telefono1");
      if (form.pac_familiar && !NAME_RE.test(form.pac_familiar.trim())) e.pac_familiar = "El nombre solo puede contener letras";
    }
    if (target === 3) { req("afectado_terremoto", "Seleccione una opción"); req("estado_actual", "Seleccione una opción"); req("ya_amputado", "Seleccione una opción"); }
    if (target === 4) {
      req("miembro_afectado", "Seleccione una opción"); req("lado_afectado", "Seleccione una opción"); req("estado_munon", "Seleccione una opción");
      if (showsInferior) req("nivel_inferior", "Seleccione el nivel de amputación");
      if (showsSuperior) req("nivel_superior", "Seleccione el nivel de amputación");
    }
    if (target === 5 && photos.length < 1) e.fotos = "Adjunte al menos una fotografía";
    if (target === 6) { req("menor_edad", "Seleccione una opción"); req("apoyo_familiar", "Seleccione una opción"); req("puede_asistir", "Seleccione una opción"); }
    if (target === 7 && !form.consentimiento) e.consentimiento = "Debe autorizar el consentimiento";
    return e;
  }

  const next = () => {
    const e = validate(step);
    if (Object.keys(e).length) {
      setErrors(e);
      toast.error("Complete los campos obligatorios marcados.");
      window.scrollTo(0, 0);
      return;
    }
    setErrors({});
    setStep((s) => s + 1);
  };
  const back = () => { setStep((s) => Math.max(0, s - 1)); };

  async function handleFiles(fileList) {
    const files = Array.from(fileList).slice(0, 2 - photos.length);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      try {
        const blob = await compressImage(file);
        const fd = new FormData();
        const name = file.name.replace(/\.(heic|heif)$/i, ".jpg");
        fd.append("file", blob, name);
        const { data } = await api.post("/patients/photo", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPhotos((p) => [...p, { file_id: data.file_id, url: URL.createObjectURL(blob) }]);
        setErrors((e) => ({ ...e, fotos: undefined }));
      } catch (err) {
        toast.error(apiError(err.response?.data?.detail) || "No se pudo subir la imagen.");
      }
    }
    setUploading(false);
  }

  const removePhoto = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));

  async function submit() {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        pac_edad: Number(form.pac_edad),
        pac_altura: Number(form.pac_altura),
        pac_peso: Number(form.pac_peso),
        fotos: photos.map((p) => p.file_id),
      };
      const { data } = await api.post("/patients", payload);
      setNumero(data.numero_paciente);
      localStorage.removeItem(STORAGE);
      setStep(9);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "No se pudo enviar el registro. Verifique su conexión e intente de nuevo.");
    } finally {
      setSubmitting(false);
    }
  }

  function resetAll() {
    setForm(EMPTY);
    setPhotos([]);
    setErrors({});
    setNumero("");
    localStorage.removeItem(STORAGE);
    setStep(0);
  }

  // ---------- Welcome ----------
  if (step === 0) {
    return (
      <Shell noProgress hideHeader>
        <div className="flex flex-col items-center text-center mb-6">
          <img src="/logo-proam.jpg" alt="PROAM Venezuela — Programa Humanitario de Prótesis para miembros amputados. Devolviendo movilidad, restaurando esperanza." className="w-full max-w-sm h-auto" />
        </div>
        <div className="space-y-4 text-slate-700 text-base leading-relaxed">
          <p>Esta iniciativa humanitaria une esfuerzos de profesionales de la salud, organizaciones, empresas, iglesias y fundaciones para ayudar a pacientes con amputaciones de miembros superiores e inferiores. La información permitirá realizar una preevaluación técnica, clasificar la prioridad del caso y preparar el expediente para la búsqueda de patrocinadores.</p>
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
            <strong>Importante:</strong> Este programa está dirigido exclusivamente a personas con amputaciones de miembros superiores o inferiores que requieren una <strong>prótesis externa</strong>. No aplica para prótesis o implantes óseos u otros dispositivos quirúrgicos.
          </div>
          <p className="text-sm text-slate-500">Completar este registro no garantiza automáticamente la entrega de una prótesis, pero permite que el caso sea evaluado dentro del programa. Coordinado por el Ing. William Rodríguez, junto a Riobot Corp. y Orthotoi (EE. UU.).</p>
        </div>
        <BottomBar>
          <button onClick={next} data-testid="wizard-start" className="btn-primary w-full">Comenzar registro</button>
        </BottomBar>
      </Shell>
    );
  }

  // ---------- Confirmation ----------
  if (step === 9) {
    return (
      <Shell noProgress>
        <div className="flex flex-col items-center text-center py-6">
          <CheckCircle2 className="h-16 w-16 text-green-600 mb-4" />
          <h2 className="font-heading text-2xl font-bold text-slate-900">Registro completado exitosamente</h2>
          <p className="text-slate-600 mt-2">Guarde este número de paciente como referencia. El caso será revisado y clasificado por el equipo del programa.</p>
          <div className="mt-6 w-full rounded-xl border-2 border-orange-600 bg-orange-50 p-6">
            <p className="text-sm text-slate-500 mb-1">Número de paciente</p>
            <p className="font-heading text-4xl font-extrabold text-orange-700 tracking-wide" data-testid="confirmation-numero">{numero}</p>
            <button
              onClick={() => { navigator.clipboard?.writeText(numero); toast.success("Número copiado"); }}
              data-testid="copy-numero"
              className="mt-4 inline-flex items-center gap-2 text-orange-700 font-medium"
            >
              <Copy className="h-4 w-4" /> Copiar número
            </button>
          </div>
          <button onClick={resetAll} data-testid="register-another" className="btn-primary w-full mt-8">Registrar otro paciente</button>
        </div>
      </Shell>
    );
  }

  // ---------- Summary ----------
  if (step === 8) {
    const Row = ({ label, value }) => (
      <div className="flex justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
        <span className="text-slate-500 text-sm">{label}</span>
        <span className="text-slate-900 font-medium text-sm text-right">{value || "—"}</span>
      </div>
    );
    const Block = ({ title, editStep, children }) => (
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-heading font-semibold text-slate-900">{title}</h3>
          <button onClick={() => setStep(editStep)} data-testid={`edit-block-${editStep}`} className="text-orange-600 text-sm inline-flex items-center gap-1">
            <Pencil className="h-3.5 w-3.5" /> Editar
          </button>
        </div>
        {children}
      </div>
    );
    return (
      <Shell noProgress>
        <h2 className="font-heading text-2xl font-bold text-slate-900 mb-1">Revisar y confirmar</h2>
        <p className="text-slate-600 mb-5 text-sm">Verifique la información antes de enviar. Puede editar cualquier bloque.</p>
        <div className="space-y-4">
          <Block title="Persona que registra" editStep={1}>
            <Row label="Nombre" value={form.reg_nombre} /><Row label="Relación" value={form.reg_relacion} /><Row label="Teléfono" value={form.reg_telefono} />
          </Block>
          <Block title="Datos del paciente" editStep={2}>
            <Row label="Nombre" value={form.pac_nombre} /><Row label="Cédula" value={form.pac_cedula} /><Row label="Edad" value={form.pac_edad} />
            <Row label="Sexo" value={form.pac_sexo} /><Row label="Altura" value={form.pac_altura && `${form.pac_altura} cm`} /><Row label="Peso" value={form.pac_peso && `${form.pac_peso} kg`} />
            <Row label="Estado" value={form.pac_estado} /><Row label="Ciudad" value={form.pac_ciudad} /><Row label="Teléfono" value={form.pac_telefono1} /><Row label="Familiar" value={form.pac_familiar} />
          </Block>
          <Block title="Información médica" editStep={3}>
            <Row label="Afectado por terremoto" value={form.afectado_terremoto} /><Row label="Estado actual" value={form.estado_actual} /><Row label="Ya amputado" value={form.ya_amputado} /><Row label="Fecha amputación" value={formatDate(form.fecha_amputacion)} /><Row label="Hospital" value={form.hospital} />
          </Block>
          <Block title="Amputación" editStep={4}>
            <Row label="Miembro" value={form.miembro_afectado} /><Row label="Lado" value={form.lado_afectado} />
            {showsInferior && <Row label="Nivel inferior" value={form.nivel_inferior} />}
            {showsSuperior && <Row label="Nivel superior" value={form.nivel_superior} />}
            <Row label="Estado del muñón" value={form.estado_munon} />
          </Block>
          <Block title="Fotografías" editStep={5}>
            <div className="flex gap-3 flex-wrap">
              {photos.map((p, i) => <img key={i} src={p.url} alt="" className="h-20 w-20 object-cover rounded-lg border border-slate-200" />)}
            </div>
          </Block>
          <Block title="Condición social" editStep={6}>
            <Row label="Menor de edad" value={form.menor_edad} /><Row label="Apoyo familiar" value={form.apoyo_familiar} /><Row label="Puede asistir" value={form.puede_asistir} />
          </Block>
        </div>
        <BottomBar>
          <button onClick={back} data-testid="wizard-back" className="btn-secondary"><ArrowLeft className="h-5 w-5" /></button>
          <button onClick={submit} disabled={submitting} data-testid="wizard-submit" className="btn-primary flex-1">
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Enviar registro"}
          </button>
        </BottomBar>
      </Shell>
    );
  }

  // ---------- Blocks ----------
  return (
    <Shell step={step}>
      {step === 1 && (
        <Section title="Persona que registra el caso" subtitle="Datos de quien completa este registro.">
          <TextField label="Nombre completo" required value={form.reg_nombre} onChange={(v) => set("reg_nombre", v.replace(/[^A-Za-zÀ-ÿñÑ\s.'-]/g, ""))} error={errors.reg_nombre} testId="f-reg_nombre" />
          <Dropdown label="Relación con el paciente" required options={RELACIONES} value={form.reg_relacion} onChange={(v) => set("reg_relacion", v)} error={errors.reg_relacion} testId="f-reg_relacion" />
          <TextField label="Teléfono de contacto" required type="tel" value={form.reg_telefono} onChange={(v) => set("reg_telefono", v)} error={errors.reg_telefono} testId="f-reg_telefono" />
        </Section>
      )}
      {step === 2 && (
        <Section title="Datos básicos del paciente">
          <TextField label="Nombre completo" required value={form.pac_nombre} onChange={(v) => set("pac_nombre", v.replace(/[^A-Za-zÀ-ÿñÑ\s.'-]/g, ""))} error={errors.pac_nombre} testId="f-pac_nombre" />
          <TextField label="Cédula o documento de identidad" hint="Si posee (ej: V-12345678)" value={form.pac_cedula} onChange={(v) => set("pac_cedula", v.replace(/[^0-9VEJGPvejgp-]/g, "").toUpperCase())} error={errors.pac_cedula} testId="f-pac_cedula" />
          <TextField label="Edad" required type="number" value={form.pac_edad} onChange={(v) => set("pac_edad", v.replace(/[^\d]/g, ""))} error={errors.pac_edad} testId="f-pac_edad" />
          <RadioCards label="Sexo" required options={["Masculino", "Femenino"]} value={form.pac_sexo} onChange={(v) => set("pac_sexo", v)} error={errors.pac_sexo} testId="f-pac_sexo" />
          <TextField label="Altura aproximada (cm)" required type="number" value={form.pac_altura} onChange={(v) => set("pac_altura", v.replace(/[^\d.]/g, ""))} error={errors.pac_altura} testId="f-pac_altura" />
          <TextField label="Peso aproximado (kg)" required type="number" value={form.pac_peso} onChange={(v) => set("pac_peso", v.replace(/[^\d.]/g, ""))} error={errors.pac_peso} testId="f-pac_peso" />
          <SearchableDropdown label="Estado" required options={ESTADOS_VE} value={form.pac_estado} onChange={(v) => set("pac_estado", v)} error={errors.pac_estado} testId="f-pac_estado" placeholder="Seleccione o busque un estado" />
          <TextField label="Ciudad donde se encuentra actualmente" value={form.pac_ciudad} onChange={(v) => set("pac_ciudad", v)} testId="f-pac_ciudad" />
          <TextField label="Teléfono principal" required type="tel" value={form.pac_telefono1} onChange={(v) => set("pac_telefono1", v.replace(/[^\d+\-\s]/g, ""))} error={errors.pac_telefono1} testId="f-pac_telefono1" />
          <TextField label="Nombre del familiar responsable" hint="Si aplica" value={form.pac_familiar} onChange={(v) => set("pac_familiar", v.replace(/[^A-Za-zÀ-ÿñÑ\s.'-]/g, ""))} error={errors.pac_familiar} testId="f-pac_familiar" />
        </Section>
      )}
      {step === 3 && (
        <Section title="Información médica básica">
          <RadioCards label="¿El paciente fue afectado directamente por el terremoto del 24 de junio de 2026?" required options={AFECTADO_OPTS} value={form.afectado_terremoto} onChange={(v) => set("afectado_terremoto", v)} error={errors.afectado_terremoto} testId="f-afectado_terremoto" />
          <RadioCards label="Estado actual del paciente" required options={ESTADO_ACTUAL_OPTS} value={form.estado_actual} onChange={(v) => set("estado_actual", v)} error={errors.estado_actual} testId="f-estado_actual" />
          <RadioCards label="¿El paciente ya fue amputado?" required options={SI_NO} value={form.ya_amputado} onChange={(v) => set("ya_amputado", v)} error={errors.ya_amputado} testId="f-ya_amputado" />
          {form.ya_amputado === "Sí" && (
            <div>
              <label className="block font-heading font-semibold text-slate-900 mb-2">Fecha aproximada de la amputación</label>
              <input
                type="date"
                value={form.fecha_amputacion}
                onChange={(e) => set("fecha_amputacion", e.target.value)}
                onClick={(e) => { try { e.target.showPicker && e.target.showPicker(); } catch (_) {} }}
                data-testid="f-fecha_amputacion"
                className="w-full h-14 px-4 text-lg rounded-lg border border-slate-300 bg-white outline-none focus:ring-4 focus:ring-orange-200 focus:border-orange-600 cursor-pointer"
              />
            </div>
          )}
          <TextField label="Hospital donde fue atendido" value={form.hospital} onChange={(v) => set("hospital", v)} testId="f-hospital" />
        </Section>
      )}
      {step === 4 && (
        <Section title="Información de la amputación">
          <Dropdown label="Miembro afectado" required options={MIEMBRO_OPTS} value={form.miembro_afectado} onChange={(v) => set("miembro_afectado", v)} error={errors.miembro_afectado} testId="f-miembro_afectado" />
          <Dropdown label="Lado afectado" required options={LADO_OPTS} value={form.lado_afectado} onChange={(v) => set("lado_afectado", v)} error={errors.lado_afectado} testId="f-lado_afectado" />
          {showsInferior && (
            <Dropdown label="Nivel de amputación (miembro inferior)" required options={NIVEL_INFERIOR_OPTS} value={form.nivel_inferior} onChange={(v) => set("nivel_inferior", v)} error={errors.nivel_inferior} testId="f-nivel_inferior" />
          )}
          {showsSuperior && (
            <Dropdown label="Nivel de amputación (miembro superior)" required options={NIVEL_SUPERIOR_OPTS} value={form.nivel_superior} onChange={(v) => set("nivel_superior", v)} error={errors.nivel_superior} testId="f-nivel_superior" />
          )}
          <Dropdown label="Estado actual del muñón" required options={MUNON_OPTS} value={form.estado_munon} onChange={(v) => set("estado_munon", v)} error={errors.estado_munon} testId="f-estado_munon" />
        </Section>
      )}
      {step === 5 && (
        <Section title="Fotografías del paciente">
          <p className="text-slate-600 text-sm -mt-2">
            Adjunte una o dos fotografías recientes donde se observe claramente: (1) el paciente de cuerpo completo y su rostro, (2) el miembro afectado.
          </p>
          <div className="grid grid-cols-2 gap-3">
            {photos.map((p, i) => (
              <div key={i} className="relative rounded-lg overflow-hidden border border-slate-200 aspect-square">
                <img src={p.url} alt="" className="h-full w-full object-cover" />
                <button onClick={() => removePhoto(i)} data-testid={`remove-photo-${i}`} className="absolute top-1.5 right-1.5 h-8 w-8 rounded-full bg-white/90 flex items-center justify-center shadow">
                  <X className="h-4 w-4 text-red-600" />
                </button>
              </div>
            ))}
            {photos.length < 2 && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="add-photo"
                className="aspect-square rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-2 text-slate-500 hover:border-orange-400 disabled:opacity-60"
              >
                {uploading ? <Loader2 className="h-7 w-7 animate-spin" /> : <Camera className="h-7 w-7" />}
                <span className="text-sm">{uploading ? "Subiendo…" : "Agregar foto"}</span>
              </button>
            )}
          </div>
          <input ref={fileInputRef} type="file" accept="image/*,.heic,.heif" capture="environment" multiple className="hidden" onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
          {errors.fotos && <p className="text-sm text-red-600" data-testid="field-error">{errors.fotos}</p>}
        </Section>
      )}
      {step === 6 && (
        <Section title="Condición social">
          <RadioCards label="¿Es menor de edad?" required options={SI_NO} value={form.menor_edad} onChange={(v) => set("menor_edad", v)} error={errors.menor_edad} testId="f-menor_edad" />
          <RadioCards label="¿Cuenta con apoyo familiar?" required options={APOYO_OPTS} value={form.apoyo_familiar} onChange={(v) => set("apoyo_familiar", v)} error={errors.apoyo_familiar} testId="f-apoyo_familiar" />
          <RadioCards label="¿Podrá asistir a una evaluación presencial cuando sea convocado?" required options={ASISTIR_OPTS} value={form.puede_asistir} onChange={(v) => set("puede_asistir", v)} error={errors.puede_asistir} testId="f-puede_asistir" />
        </Section>
      )}
      {step === 7 && (
        <Section title="Consentimiento">
          <button
            type="button"
            onClick={() => set("consentimiento", !form.consentimiento)}
            data-testid="f-consentimiento"
            className={`w-full flex items-start gap-3 p-4 rounded-lg border-2 text-left transition-colors ${form.consentimiento ? "border-orange-600 bg-orange-50" : "border-slate-300 bg-white"}`}
          >
            <span className={`mt-0.5 h-6 w-6 rounded border-2 shrink-0 flex items-center justify-center ${form.consentimiento ? "bg-orange-600 border-orange-600" : "border-slate-400"}`}>
              {form.consentimiento && <CheckCircle2 className="h-5 w-5 text-white" />}
            </span>
            <span className="text-slate-800">Autorizo el uso de esta información únicamente para fines del Programa Humanitario de Prótesis.</span>
          </button>
          {errors.consentimiento && <p className="text-sm text-red-600" data-testid="field-error">{errors.consentimiento}</p>}
        </Section>
      )}

      <BottomBar>
        <button onClick={back} data-testid="wizard-back" className="btn-secondary"><ArrowLeft className="h-5 w-5" /></button>
        {step < 7 ? (
          <button onClick={next} data-testid="wizard-next" className="btn-primary flex-1">Siguiente <ArrowRight className="h-5 w-5" /></button>
        ) : (
          <button onClick={next} data-testid="wizard-review" className="btn-primary flex-1">Revisar registro <ArrowRight className="h-5 w-5" /></button>
        )}
      </BottomBar>
    </Shell>
  );
}

function Shell({ children, step, noProgress, hideHeader }) {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      {!hideHeader && (
        <header className="sticky top-0 z-20 bg-white border-b border-slate-200">
          <div className="max-w-lg mx-auto px-5 py-2.5 flex items-center justify-between">
            <img src="/logo-mark.jpg" alt="PROAM Venezuela" className="h-9 w-auto" />
            {!noProgress && <span className="text-sm text-slate-500 font-medium" data-testid="progress-label">Bloque {step} de 7</span>}
          </div>
          {!noProgress && (
            <div className="h-2 bg-slate-100">
              <div className="h-full bg-orange-600 transition-all" style={{ width: `${(step / 7) * 100}%` }} />
            </div>
          )}
        </header>
      )}
      <main className="flex-1 max-w-lg w-full mx-auto px-5 py-6 pb-32">{children}</main>
      <style>{`
        .btn-primary{height:56px;border-radius:.5rem;background:#ea580c;color:#fff;font-weight:600;font-family:'Work Sans',sans-serif;font-size:1.125rem;display:flex;align-items:center;justify-content:center;gap:.5rem;transition:background-color .15s;}
        .btn-primary:hover{background:#c2410c}
        .btn-primary:disabled{opacity:.6}
        .btn-secondary{height:56px;width:56px;border-radius:.5rem;border:1px solid #cbd5e1;background:#fff;color:#334155;display:flex;align-items:center;justify-content:center;transition:background-color .15s;}
        .btn-secondary:hover{background:#f1f5f9}
      `}</style>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div>
      <h2 className="font-heading text-2xl font-bold text-slate-900 mb-1">{title}</h2>
      {subtitle && <p className="text-slate-500 mb-5 text-sm">{subtitle}</p>}
      <div className={subtitle ? "space-y-6" : "space-y-6 mt-5"}>{children}</div>
    </div>
  );
}

function BottomBar({ children }) {
  return (
    <div className="fixed bottom-0 left-0 right-0 z-20 bg-white border-t border-slate-200">
      <div className="max-w-lg mx-auto px-5 py-3 flex gap-3">{children}</div>
    </div>
  );
}
